import React, { useMemo, useState } from 'react'
import {
  BasicStyledComponent,
  DSProductComparisonChartData,
  DSProductComparisonChartProduct,
} from 'shared-definitions/types'
import styles from './ProductComparisonChart.module.css'
import clsx from 'clsx'
import { FieldSelect } from '../form/FieldSelect'
import ProductComparisonChartIconsOverlay, {
  ProductComparisonChartIconsOverlayGroup,
} from './ProductComparisonChartIconsOverlay'
import { useOutsideClick } from '../hooks/use-outside-click'
import { CheckDetailsProductCell } from './CheckDetailsProductCell'
import AccordionElement from '../layout/Accordion'

export interface ProductComparisonChartProps extends BasicStyledComponent {
  popupTitle: string
  data: DSProductComparisonChartData
  onSeeMore: (productId: string) => void
}

export const ProductComparisonChart: React.FC<ProductComparisonChartProps> = ({
  className,
  data,
  onSeeMore,
}) => {
  const [sortYField, setSortYField] = useState(data.defaultYSort)
  const [sortXField, setSortXField] = useState(data.defaultXSort)
  const [selectedGroupProducts, setSelectedGroupProducts] = useState<
    DSProductComparisonChartProduct[]
  >([])
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({})

  const [userIntentionHideDescription, setUserIntentionHideDescription] = useState(false)
  const ref = useOutsideClick<HTMLDivElement>(() => {
    setUserIntentionHideDescription(true)
  }, [])

  const ALLOWED_GROUP_DEVIATION_X = 0.4,
    ALLOWED_GROUP_DEVIATION_Y = 0.25
  const filteredGroupedProducts = useMemo(
    () =>
      groupProductsByXY(
        data.products,
        ALLOWED_GROUP_DEVIATION_X,
        ALLOWED_GROUP_DEVIATION_Y,
        sortXField,
        sortYField
      ),
    [data.products, sortXField, sortYField]
  )

  return (
    <div className={clsx(styles.container, className)}>
      <FieldSelect
        className={styles.sortYSelect}
        value={sortYField}
        onChange={v => {
          setSelectedGroupProducts([])
          setSortYField(v ?? data.defaultYSort)
        }}
        field={{
          required: false,
          name: 'sortY',
          placeholder: null,
          options: data.sortColumns,
        }}
      />

      <FieldSelect
        className={styles.sortXSelect}
        value={sortXField}
        onChange={v => {
          setSelectedGroupProducts([])
          setSortXField(v ?? data.defaultXSort)
        }}
        field={{
          required: false,
          name: 'sortX',
          placeholder: null,
          options: data.sortColumns,
        }}
      />

      <div className={styles.gridBackground}>
        <ProductComparisonChartIconsOverlay
          onProductGroupClick={products => {
            setSelectedGroupProducts(products)
            setUserIntentionHideDescription(false)
            setExpandedRows({})
          }}
          className={styles.iconsOverlay}
          groupedProducts={filteredGroupedProducts}
        />
        <div
          ref={ref}
          className={clsx(styles.descriptionOverlay, {
            [styles.descriptionOverlayEmpty]: !selectedGroupProducts.length,
            [styles.descriptionOverlayHide]: userIntentionHideDescription,
          })}
        >
          <div className={styles.descriptionBox}>
            {selectedGroupProducts.map(product => (
              <div key={product.id} className={clsx(styles.descriptionBoxItem)}>
                <button
                  type="button"
                  className={clsx(styles.descriptionBoxItemExpandButton, {
                    [styles.descriptionBoxItemExpandButtonExpanded]: expandedRows[product.id],
                  })}
                  onClick={() => {
                    setExpandedRows(rows => {
                      if (rows[product.id]) {
                        return {}
                      }
                      return {
                        [product.id]: true,
                      }
                    })
                  }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                  >
                    <path
                      d="M4.00547 5.33032C3.45614 5.33032 3.155 5.95365 3.48467 6.39298C3.98467 7.05965 6.9846 11.0596 7.48467 11.7263C7.75133 12.0816 8.28047 12.0816 8.54713 11.7263L12.5471 6.39298C12.8767 5.95365 12.5549 5.33032 12.0055 5.33032H4.00547Z"
                      fill="currentColor"
                    />
                  </svg>
                </button>
                <CheckDetailsProductCell
                  variant="compact"
                  onSeeMore={onSeeMore}
                  product={product}
                />
                <AccordionElement
                  className={clsx(styles.descriptionCollapsibleBox, {
                    [styles.descriptionCollapsibleBoxCollapsed]: !expandedRows[product.id],
                  })}
                  open={expandedRows[product.id]}
                >
                  <ul className={styles.descriptionValuesBox}>
                    {data.sortColumns
                      .filter(i => [sortXField, sortYField].includes(i.value))
                      .map(column => (
                        <li key={column.value} className={styles.descriptionValue}>
                          <span
                            className={clsx(
                              styles.descriptionValueName,
                              styles.descriptionValueValue
                            )}
                          >
                            {column.label}
                          </span>{' '}
                          <span className={styles.descriptionValueValue}>
                            {product.sortFields[column.value].displayValue}
                          </span>
                        </li>
                      ))}
                  </ul>
                </AccordionElement>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

function groupProductsByXY(
  products: DSProductComparisonChartProduct[],
  deviationX: number,
  deviationY: number,
  sortXField: string,
  sortYField: string
): ProductComparisonChartIconsOverlayGroup[] {
  const yVals = products.map(product => product.sortFields[sortYField].rawValue),
    xVals = products.map(product => product.sortFields[sortXField].rawValue),
    minYVal = Math.min(...yVals),
    maxYVal = Math.max(...yVals),
    minXVal = Math.min(...xVals),
    maxXVal = Math.max(...xVals)

  const normalizedProducts = products.map(product => ({
    product,
    x:
      minXVal === maxXVal
        ? 0
        : (product.sortFields[sortXField].rawValue - minXVal) / (maxXVal - minXVal),
    y:
      minYVal === maxYVal
        ? 0
        : (product.sortFields[sortYField].rawValue - minYVal) / (maxYVal - minYVal),
  }))

  const groups: ProductComparisonChartIconsOverlayGroup[] = []
  normalizedProducts.forEach(product => {
    const group = groups.find(
      g => Math.abs(g.x - product.x) < deviationX && Math.abs(g.y - product.y) < deviationY
    )
    if (group) {
      const ids = group.products.map(p => p.id)
      ids.push(product.product.id)
      ids.sort((a, b) => a.localeCompare(b))
      group.key = ids.join('-')

      group.products.push(product.product)
    } else {
      groups.push({
        x: product.x,
        y: product.y,
        key: product.product.id,
        products: [product.product],
      })
    }
  })

  // for every group set x and y to the average of the group
  groups.forEach(group => {
    group.x =
      group.products.reduce((acc, p) => {
        const xVal =
          minXVal === maxXVal
            ? 0
            : (p.sortFields[sortXField].rawValue - minXVal) / (maxXVal - minXVal)
        return acc + xVal
      }, 0) / group.products.length
    group.y =
      group.products.reduce((acc, p) => {
        const yVal =
          minYVal === maxYVal
            ? 0
            : (p.sortFields[sortYField].rawValue - minYVal) / (maxYVal - minYVal)
        return acc + yVal
      }, 0) / group.products.length
  })

  return groups
}
